import { Art } from "./Art";
import { TekstSchematu } from "./TekstSchematu";
import { DocumentLink } from "./DocumentLink";
import { t } from "../i18n";
import { Icons } from "../lib/icons";
import type { AnsweredField, BrandText, Datapoint, Locale } from "../types";

/**
 * Ekran końca ścieżki. Nota `severity: final` to nie jest „kolejne pole" —
 * to moment, w którym człowiek chce wiedzieć dwie rzeczy: czy się udało
 * i co się teraz stanie. Dostaje więc pełny ekran, nie kartę w kaskadzie.
 */
export function Outcome({
  datapoint,
  locale,
  documents = [],
  recordId,
  onBack,
  busy = false,
  final,
}: {
  datapoint: Datapoint;
  locale: Locale;
  /** Podpisane dokumenty tego procesu — do wglądu i pobrania. */
  documents?: AnsweredField[];
  recordId?: string;
  /** Cofnięcie ostatniej odpowiedzi — tylko dla ślepego zaułka („dalej nie możemy"),
   *  gdy kandydat trafił tu przez pomyłkowy klik i chce zmienić wybór. */
  onBack?: () => void;
  busy?: boolean;
  /** Finał z szablonu marki. Przy sukcesie marka daje tylko tytuł — treść
   *  zostaje z noty końcowej schematu (`koniec_*`), bo to ona mówi, co dalej
   *  na tej ścieżce: że zezwolenie trwa 2–3 miesiące i do tego czasu nie
   *  wolno jeździć, że pierwsza wypłata przy oświadczeniu przesunie się.
   *  Ogólny tekst marki w tym miejscu ukrywał te informacje. Ślepy zaułek
   *  mówi w całości swoją treścią — to ona tłumaczy, czemu dalej nie możemy. */
  final?: BrandText;
}) {
  const blocked = datapoint.blocking;
  const marka = blocked ? undefined : final;
  const tytul = marka?.title ?? datapoint.title;
  const tekst = datapoint.subtitle;

  return (
    <div className="outcome" role={blocked ? "alert" : "status"}>
      {blocked ? (
        <div className="outcome__badge outcome__badge--stop">
          <Icons.cross size={30} />
        </div>
      ) : (
        <Art name="gotowe" size={180} className="outcome__art" />
      )}

      <h2 className="outcome__title">{tytul}</h2>
      {tekst && <TekstSchematu tekst={tekst} className="outcome__text" />}

      {/* Człowiek właśnie coś podpisał i schodził z ekranu bez możliwości
          zobaczenia tego dokumentu. Kopia idzie mailem, ale czekanie na maila,
          żeby przeczytać własne oświadczenie, jest absurdem. */}
      {/* Kaskada z tokens.css — dokumenty siadają jeden po drugim,
          po wejściu tytułu (bazowe opóźnienie w public.css). */}
      {!blocked && recordId && documents.length > 0 && (
        <div className="outcome__docs kaskada">
          <p className="outcome__docstitle">{t("signedDocuments", locale)}</p>
          {documents.map((document, index) => (
            <DocumentLink
              key={document.key}
              recordId={recordId}
              docKey={document.key}
              locale={locale}
              style={{ "--i": index + 1 } as React.CSSProperties}
              className="btn btn--secondary outcome__doc"
            >
              <Icons.document size={18} />
              {document.title}
            </DocumentLink>
          ))}
        </div>
      )}

      <div className="outcome__next">
        <Icons.spark size={20} />
        <span>{blocked ? t("outcomeBlockedNext", locale) : t("outcomeDoneNext", locale)}</span>
      </div>

      {/* Ślepy zaułek bywa skutkiem pomyłkowego kliknięcia — dajemy drogę powrotu
          do ostatniego pytania, żeby kandydat mógł poprawić wybór bez pisania do nas. */}
      {blocked && onBack && (
        <button className="btn btn--secondary outcome__back" disabled={busy} onClick={onBack}>
          {t("outcomeBack", locale)}
        </button>
      )}
    </div>
  );
}
